import { useKodeListe } from "@/hooks/useQueries";
import { formatLessons } from "@/util/number";
import { useMemo } from "react";

import type { DecoratedPersonEfteruddannelse } from "./decoratePeriod";
import { EditOverfoerteLektionerModal } from "./EditOverfoerteLektionerModal";
import {
  buildPeriodLessonTypeRows,
  sumPeriodLessonTypeColumn,
} from "./periodLessonTypeUtils";

interface PeriodLessonTypeSummaryCardProps {
  personId: string;
  period: DecoratedPersonEfteruddannelse;
  allPeriods: DecoratedPersonEfteruddannelse[];
  harForegaaendePeriode: boolean;
  lektionerIndberettetForrigePeriode?: number | null;
  onSaved: () => void;
}

export const PeriodLessonTypeSummaryCard = ({
  personId,
  period,
  allPeriods,
  harForegaaendePeriode,
  lektionerIndberettetForrigePeriode,
  onSaved,
}: PeriodLessonTypeSummaryCardProps) => {
  const { kodeliste: efteruddannelsestyper, getKodelisteIsLoading } =
    useKodeListe("Efteruddannelsestype");
  const rows = useMemo(
    () => buildPeriodLessonTypeRows(period, efteruddannelsestyper, allPeriods),
    [period, efteruddannelsestyper, allPeriods],
  );
  const totalReported = useMemo(
    () => sumPeriodLessonTypeColumn(rows, "reported"),
    [rows],
  );
  const totalTransferredOut = useMemo(
    () => sumPeriodLessonTypeColumn(rows, "transferredOut"),
    [rows],
  );

  // "Overført fra forrige periode" er alt overført ind i perioden: Særlig plus påbud/pålæg. Modalen redigerer kun
  // Særlig-delen og viser påbud/pålæg read-only, så opdelingen hører til dér, ikke i oversigten.
  const totalTransferredIn = useMemo(
    () => sumPeriodLessonTypeColumn(rows, "transferredIn"),
    [rows],
  );

  return (
    <div className="c-card p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h3 className="h5 mb-0">Lektionsoversigt</h3>
        <EditOverfoerteLektionerModal
          personId={personId}
          period={period}
          harForegaaendePeriode={harForegaaendePeriode}
          lektionerIndberettetForrigePeriode={
            lektionerIndberettetForrigePeriode
          }
          onSaved={onSaved}
        />
      </div>
      {getKodelisteIsLoading ? (
        <p className="text-muted mb-0">Indlæser typer...</p>
      ) : !rows.length ? (
        <p className="text-muted mb-0">
          Ingen lektioner fordelt på typer for denne periode.
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Type</th>
                <th className="text-end">Overført fra forrige periode</th>
                <th className="text-end">Indberettet</th>
                <th className="text-end">Overført til næste periode</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.typeNr}>
                  <td>{row.typeLabel}</td>
                  <td className="text-end">
                    {formatLessons(row.transferredIn)}
                  </td>
                  <td className="text-end">{formatLessons(row.reported)}</td>
                  <td className="text-end">
                    {formatLessons(row.transferredOut)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="fw-semibold table-secondary">
                <td>I alt</td>
                <td className="text-end">
                  {formatLessons(totalTransferredIn)}
                </td>
                <td className="text-end">{formatLessons(totalReported)}</td>
                <td className="text-end">
                  {formatLessons(totalTransferredOut)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
